import { useEffect, useRef, useState, type ChangeEvent } from "react";
import Modal from "@/components/base/Modal";
import { inputClass, labelClass, ghostButtonClass, primaryButtonClass } from "@/components/base/fieldStyles";
import type { Product } from "@/types";
import { formatCOP } from "@/utils/format";
import { fileToThumb } from "@/utils/image";
import NumberInput from "@/components/base/NumberInput";
import { DEFAULT_CATEGORIES } from "@/utils/categories";

interface FormState {
  sku: string;
  name: string;
  category: string;
  pv: number;
  costBase: number;
  pricePublic: number;
  imageUrl: string;
}

const EMPTY: FormState = {
  sku: "",
  name: "",
  category: "Hogar",
  pv: 0,
  costBase: 0,
  pricePublic: 0,
  imageUrl: "",
};

interface ProductFormModalProps {
  open: boolean;
  product: Product | null;
  categories?: string[];
  onClose: () => void;
  onSubmit: (product: Product) => void;
}

const ProductFormModal = ({ open, product, categories, onClose, onSubmit }: ProductFormModalProps) => {
  const categoryOptions = categories && categories.length > 0 ? categories : DEFAULT_CATEGORIES;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setUploadError("");
    setForm(
      product
        ? {
            sku: product.sku,
            name: product.name,
            category: product.category,
            pv: product.pv,
            costBase: product.costBase,
            pricePublic: product.pricePublic,
            imageUrl: product.imageUrl ?? "",
          }
        : EMPTY
    );
  }, [open, product]);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const onPickImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError("");
    try {
      const dataUrl = await fileToThumb(file);
      set({ imageUrl: dataUrl });
    } catch {
      setUploadError("No se pudo cargar la imagen. Prueba con un archivo más pequeño.");
    }
  };

  const margin =
    form.pricePublic > 0
      ? ((form.pricePublic - form.costBase) / form.pricePublic) * 100
      : 0;

  const submit = () => {
    if (!form.sku.trim() || !form.name.trim()) {
      setError("El SKU y el nombre del producto son obligatorios.");
      return;
    }
    onSubmit({
      id: product?.id ?? `new_${form.sku.trim()}`,
      sku: form.sku.trim(),
      name: form.name.trim(),
      category: form.category,
      pv: Number(form.pv) || 0,
      costBase: Number(form.costBase) || 0,
      pricePublic: Number(form.pricePublic) || 0,
      imageUrl: form.imageUrl || undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={product ? "Editar producto del catálogo" : "Nuevo producto del catálogo"}
      icon="ri-store-2-line"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-save-3-line" /> {product ? "Guardar cambios" : "Agregar al catálogo"}
          </button>
        </>
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onPickImage}
        className="hidden"
        aria-label="Subir foto del producto"
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="group relative w-24 h-24 shrink-0 rounded-lg border-2 border-dashed border-background-300/60 bg-background-200/40 flex items-center justify-center overflow-hidden cursor-pointer hover:border-accent-500/60"
          title="Clic para subir la foto del producto"
        >
          {form.imageUrl ? (
            <img src={form.imageUrl} alt="Foto del producto" className="w-full h-full object-contain" />
          ) : (
            <span className="flex flex-col items-center text-foreground-500">
              <i className="ri-image-add-line text-2xl" />
              <span className="text-[10px] mt-1">Subir foto</span>
            </span>
          )}
          <span className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <i className="ri-camera-line text-foreground-100 text-xl" />
          </span>
        </button>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground-800">Foto del producto</p>
          <p className="text-[11px] text-foreground-500 mt-0.5">
            Haz clic en la miniatura para subir o cambiar la imagen del producto.
          </p>
          {form.imageUrl && (
            <button
              type="button"
              onClick={() => set({ imageUrl: "" })}
              className="mt-1.5 text-[11px] font-semibold text-primary-200 hover:text-primary-100 cursor-pointer inline-flex items-center gap-1"
            >
              <i className="ri-delete-bin-line" /> Quitar foto
            </button>
          )}
          {uploadError && (
            <p className="text-[11px] text-primary-200 mt-1 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {uploadError}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelClass}>Nombre del producto</label>
          <input
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Ej: Nutrilite Doble X 30 días"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>SKU / Código Amway</label>
          <input
            value={form.sku}
            onChange={(e) => set({ sku: e.target.value })}
            placeholder="Ej: AMW-3101"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Categoría</label>
          <select
            value={form.category}
            onChange={(e) => set({ category: e.target.value })}
            className={inputClass}
          >
            {categoryOptions.map((c) => (
              <option key={c} value={c} className="bg-background-100">
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Puntos PV oficiales</label>
          <NumberInput value={form.pv} onChange={(n) => set({ pv: n })} className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Precio costo base (COP)</label>
          <NumberInput value={form.costBase} onChange={(n) => set({ costBase: n })} className={inputClass} />
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass}>Precio público (COP)</label>
          <NumberInput value={form.pricePublic} onChange={(n) => set({ pricePublic: n })} className={inputClass} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Ganancia estimada (25%)</p>
          <p className="text-sm font-bold text-foreground-950">
            {formatCOP(form.pricePublic * 0.25)}
          </p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Margen sobre el precio público</p>
          <p className="text-sm font-bold text-accent-200">{margin.toFixed(1)}%</p>
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default ProductFormModal;